import {useCallback, useEffect, useRef, useState} from 'react';
import {listComments} from '../../../../utils/api/comments';
import {useStompSubscription} from '../../../../hooks/useStompSubscription';

/**
 * Subscribe to comments-backed chat messages for an arbitrary
 * elementName / elementId pair (e.g. OneOnOne/{chatId}, whiteboard/{whiteboardId}).
 *
 * Fetches initial list via REST; refreshes on STOMP messages.
 * Returns { messages, isLoading, error, addOptimisticMessage }
 */
const useScopedCommentsMessages = (elementName, elementId) => {
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(Boolean(elementId));
    const [error, setError] = useState(null);
    const mountedRef = useRef(true);
    const optimisticIdRef = useRef(0);

    const load = useCallback(async () => {
        if (!elementName || !elementId) {
            setMessages([]);
            setIsLoading(false);
            return;
        }
        try {
            const data = await listComments(elementName, elementId);
            if (mountedRef.current) {
                setMessages(Array.isArray(data) ? data : []);
                setIsLoading(false);
            }
        } catch (err) {
            console.error('Scoped comments fetch error:', err);
            if (mountedRef.current) {
                setError(err);
                setIsLoading(false);
            }
        }
    }, [elementName, elementId]);

    // Optimistically append a message immediately after send, before STOMP fires.
    const addOptimisticMessage = useCallback((msg) => {
        const tempId = `optimistic-${++optimisticIdRef.current}`;
        setMessages((prev) => [
            ...prev.filter((m) => !String(m.messageId).startsWith('optimistic-')),
            {...msg, messageId: tempId},
        ]);
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        setError(null);
        setMessages([]);
        setIsLoading(Boolean(elementName && elementId));
        load();
        return () => {
            mountedRef.current = false;
        };
    }, [load, elementName, elementId]);

    // Live STOMP updates — replaces optimistic messages with server truth
    useStompSubscription(
        elementName && elementId ? `/topic/comments/${elementName}/${elementId}` : null,
        load,
    );

    return {messages, isLoading, error, addOptimisticMessage};
};

export default useScopedCommentsMessages;
